import { create } from 'zustand';
import { Node, Edge } from '@xyflow/react';
import { NodeBase, EdgeBase, RiskRecord, TaskRecord, EvidenceRecord, FilterState, RiskSeverity, RiskStatus, Domain, Purdue } from '@/types/assets';
import { supabase } from '@/integrations/supabase/client';
import dayjs from 'dayjs';

type SupabaseAssetMetadata = NonNullable<NodeBase['meta']> & {
  domain?: Domain;
  purdue_level?: Purdue;
  parentId?: string;
  category?: string;
  hierarchyType?: NodeBase['type'];
};

type RiskUpdatePayload = {
  title?: string;
  cause?: string;
  likelihood?: number;
  impact?: number;
  status?: string;
  risk_score?: number;
};

const normalizeRiskStatus = (status?: string | null): RiskStatus => {
  if (!status) return 'Open';
  const normalized = status.toLowerCase();
  switch (normalized) {
    case 'open':
      return 'Open';
    case 'mitigating':
      return 'Mitigating';
    case 'accepted':
      return 'Accepted';
    case 'closed':
      return 'Closed';
    default:
      return 'Open';
  }
};

interface AssetsState {
  // Graph data
  nodes: Node<NodeBase>[];
  edges: Edge<EdgeBase>[];
  
  // Related data
  risks: RiskRecord[];
  tasks: TaskRecord[];
  evidence: EvidenceRecord[];
  
  // UI state
  selectedNodeId: string | null;
  filters: FilterState;
  loading: boolean;
  
  // Actions
  addNode: (node: Node<NodeBase>) => Promise<void>;
  updateNode: (id: string, updates: Partial<NodeBase>) => Promise<void>;
  updateNodePosition: (id: string, position: { x: number; y: number }) => Promise<void>;
  deleteNode: (id: string) => Promise<void>;
  
  addEdge: (edge: Edge<EdgeBase>) => void;
  updateEdge: (id: string, updates: Partial<EdgeBase>) => void;
  deleteEdge: (id: string) => void;
  
  addRisk: (risk: Omit<RiskRecord, 'id' | 'created_at' | 'updated_at' | 'score' | 'severity'>) => Promise<void>;
  updateRisk: (id: string, updates: Partial<RiskRecord>) => Promise<void>;
  deleteRisk: (id: string) => Promise<void>;
  
  addTask: (task: Omit<TaskRecord, 'id'>) => void;
  updateTask: (id: string, updates: Partial<TaskRecord>) => void;
  deleteTask: (id: string) => void;
  completeTask: (id: string) => void;
  
  addEvidence: (evidence: Omit<EvidenceRecord, 'id' | 'created_at'>) => Promise<void>;
  updateEvidence: (id: string, updates: Partial<EvidenceRecord>) => void;
  deleteEvidence: (id: string) => void;
  
  setSelectedNode: (id: string | null) => void;
  updateFilters: (filters: Partial<FilterState>) => void;
  
  // Computed getters
  getNodesByParent: (parentId?: string) => Node<NodeBase>[];
  getRisksByNode: (nodeId: string) => RiskRecord[];
  getTasksByNode: (nodeId: string) => TaskRecord[];
  getEvidenceByNode: (nodeId: string) => EvidenceRecord[];
  
  // Utility
  calculateRiskScore: (likelihood: number, impact: number) => { score: number; severity: RiskSeverity };
  
  // Load data from Supabase
  loadAssets: () => Promise<void>;
  loadRisks: () => Promise<void>;
  loadEvidence: () => Promise<void>;
  
  // Persistence
  exportData: () => string;
  importData: (data: string) => void;
  
  // Initialize with seed data
  initializeSeedData: () => void;
}

const calculateRiskSeverity = (score: number): RiskSeverity => {
  if (score <= 5) return 'Low';
  if (score <= 10) return 'Medium';
  if (score <= 15) return 'High';
  return 'Critical';
};

const generateId = () => Math.random().toString(36).substring(2, 15);

export const useAssetsStore = create<AssetsState>()((set, get) => ({
  nodes: [],
  edges: [],
  risks: [],
  tasks: [],
  evidence: [],
  selectedNodeId: null,
  loading: false,
  filters: {
    types: [],
    categories: [],
    sites: [],
    zones: [],
    domains: [],
    purdueLevel: [],
    suppliers: [],
    criticality: [],
    riskStatus: [],
    taskStatus: []
  },

  addNode: async (node) => {
    try {
      // Get current user's org_id
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user?.id) {
        throw new Error('User not authenticated');
      }
      
      const { data: profile } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('user_id', userData.user.id)
        .single();
      
      if (!profile?.org_id) {
        throw new Error('User organization not found');
      }

      // Handle site creation first if needed
      let siteId = null;
      if (node.data.type === 'site') {
        // Create entry in sites table with complete data from form
        const siteData = {
          name: node.data.label,
          site_code: node.data.meta?.site_code || null,
          address: node.data.meta?.address || null,
          country: node.data.meta?.country || null,
          timezone: node.data.meta?.timezone || 'UTC',
          criticality: node.data.meta?.site_criticality || null,
          owner_id: node.data.meta?.owner_id || null,
          notes: node.data.meta?.description || null,
          org_id: profile.org_id,
          metadata: {
            coordinates: node.data.meta?.lat && node.data.meta?.lon ? {
              lat: node.data.meta.lat,
              lon: node.data.meta.lon,
            } : undefined,
            physical_security: node.data.meta?.physical_security,
            operating_hours: node.data.meta?.operating_hours,
            created_from: 'asset_management'
          }
        };

        const { data: siteResult, error: siteError } = await supabase
          .from('sites')
          .insert(siteData)
          .select()
          .single();

        if (siteError) throw siteError;
        siteId = siteResult.id;
        
        // Store the site_id in node metadata for reference
        if (!node.data.meta) node.data.meta = {};
        node.data.meta.site_id = siteId;
      } else if (node.data.parentId && node.data.type === 'zone') {
        // For zones, find the site_id from the parent site asset
        const parentNode = get().nodes.find(n => n.id === node.data.parentId);
        if (parentNode?.data.type === 'site') {
          // Check if parent has site_id in metadata
          if (parentNode.data.meta?.site_id) {
            siteId = parentNode.data.meta.site_id;
          } else {
            // Fallback: try to find by name (legacy support)
            const { data: parentSites } = await supabase
              .from('sites')
              .select('id')
              .eq('name', parentNode.data.label)
              .eq('org_id', profile.org_id)
              .limit(1);
            
            if (parentSites && parentSites.length > 0) {
              siteId = parentSites[0].id;
            }
          }
        }
      }

      // Convert React Flow node to Supabase asset format
      const assetData = {
        name: node.data.label,
        type: node.data.type,
        category: node.data.domain || node.data.meta?.zoneType || 'IT',
        site_id: siteId,
        position_x: node.position.x,
        position_y: node.position.y,
        org_id: profile.org_id,
        metadata: {
          domain: node.data.domain,
          purdue_level: node.data.purdue_level,
          category: node.data.category,
          parentId: node.data.parentId, // Store parentId in metadata for hierarchy
          zoneType: node.data.meta?.zoneType,
          hierarchyType: node.data.type, // Store original hierarchy type
          ...node.data.meta
        }
      };

      const { data, error } = await supabase
        .from('endpoints')
        .insert(assetData)
        .select()
        .single();

      if (error) throw error;

      const metadata = (data.metadata as SupabaseAssetMetadata | null) ?? null;

      // Add the new node to the store without reloading everything
      const newNode: Node<NodeBase> = {
        id: data.id,
        type: 'asset',
        position: node.position,
        data: {
          id: data.id,
          type: node.data.type,
          label: node.data.label,
          category: data.category || undefined,
          domain: metadata?.domain,
          purdue_level: metadata?.purdue_level,
          parentId: metadata?.parentId,
          meta: metadata ?? undefined
        }
      };

      set((state) => {
        const updatedNodes = [...state.nodes, newNode];
        return { nodes: updatedNodes };
      });
    } catch (error) {
      console.error('Error adding node:', error);
      throw error;
    }
  },

  updateNodePosition: async (id, position) => {
    try {
      const { error } = await supabase
        .from('endpoints')
        .update({
          position_x: position.x,
          position_y: position.y
        })
        .eq('id', id);

      if (error) throw error;

      set((state) => ({
        nodes: state.nodes.map(node => 
          node.id === id 
            ? { ...node, position }
            : node
        )
      }));
    } catch (error) {
      console.error('Error updating node position:', error);
      throw error;
    }
  },

  updateNode: async (id, updates) => {
    try {
      const { error } = await supabase
        .from('endpoints')
        .update({
          name: updates.label,
          type: updates.type,
          category: updates.category,
          metadata: updates.meta
        })
        .eq('id', id);

      if (error) throw error;

      set((state) => ({
        nodes: state.nodes.map(node => 
          node.id === id 
            ? { ...node, data: { ...node.data, ...updates } }
            : node
        )
      }));
    } catch (error) {
      console.error('Error updating node:', error);
      throw error;
    }
  },

  deleteNode: async (id) => {
    try {
      const { error } = await supabase
        .from('endpoints')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set((state) => ({
        nodes: state.nodes.filter(n => n.id !== id),
        edges: state.edges.filter(e => e.source !== id && e.target !== id),
        risks: state.risks.filter(r => r.nodeId !== id),
        evidence: state.evidence.filter(e => e.nodeId !== id)
      }));
    } catch (error) {
      console.error('Error deleting node:', error);
      throw error;
    }
  },

  addEdge: (edge) => {
    set((state) => ({
      edges: [...state.edges, edge]
    }));
  },

  updateEdge: (id, updates) => {
    set((state) => ({
      edges: state.edges.map(edge => 
        edge.id === id 
          ? { ...edge, ...(updates as Partial<Edge<EdgeBase>>) }
          : edge
      ) as Edge<EdgeBase>[]
    }));
  },

  deleteEdge: (id) => {
    set((state) => ({
      edges: state.edges.filter(e => e.id !== id)
    }));
  },

  addRisk: async (risk) => {
    try {
      // Get current user's org_id
      const user = await supabase.auth.getUser();
      if (!user.data.user?.id) {
        throw new Error('User not authenticated');
      }
      
      const { data: profile } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('user_id', user.data.user.id)
        .single();
      
      if (!profile?.org_id) {
        throw new Error('User organization not found');
      }

      const { score, severity } = get().calculateRiskScore(risk.likelihood, risk.impact);
      
      const riskData = {
        title: risk.title,
        cause: risk.description, // Map description to cause field
        asset_or_process: risk.nodeId,
        likelihood: risk.likelihood,
        impact: risk.impact,
        risk_score: score,
        status: risk.status.toLowerCase(),
        org_id: profile.org_id
      };

      const { data, error } = await supabase
        .from('risks')
        .insert(riskData)
        .select()
        .single();

      if (error) throw error;

      const newRisk: RiskRecord = {
        id: data.id,
        nodeId: risk.nodeId,
        title: risk.title,
        description: risk.description,
        likelihood: risk.likelihood,
        impact: risk.impact,
        score,
        severity,
        status: risk.status,
        created_at: data.created_at,
        updated_at: data.updated_at
      };

      set((state) => ({
        risks: [...state.risks, newRisk]
      }));
    } catch (error) {
      console.error('Error adding risk:', error);
      throw error;
    }
  },

  updateRisk: async (id, updates) => {
    try {
      const riskData: RiskUpdatePayload = {};
      
      if (updates.title) riskData.title = updates.title;
      if (updates.description) riskData.cause = updates.description;
      if (updates.likelihood) riskData.likelihood = updates.likelihood;
      if (updates.impact) riskData.impact = updates.impact;
      if (updates.status) riskData.status = updates.status.toLowerCase();
      
      if (updates.likelihood || updates.impact) {
        const { score } = get().calculateRiskScore(
          updates.likelihood || get().risks.find(r => r.id === id)?.likelihood || 3,
          updates.impact || get().risks.find(r => r.id === id)?.impact || 3
        );
        riskData.risk_score = score;
      }

      const { error } = await supabase
        .from('risks')
        .update(riskData)
        .eq('id', id);

      if (error) throw error;

      set((state) => ({
        risks: state.risks.map(risk => {
          if (risk.id === id) {
            const updatedRisk = { ...risk, ...updates, updated_at: dayjs().toISOString() };
            
            if (updates.likelihood || updates.impact) {
              const { score, severity } = get().calculateRiskScore(
                updates.likelihood || risk.likelihood,
                updates.impact || risk.impact
              );
              updatedRisk.score = score;
              updatedRisk.severity = severity;
            }
            
            return updatedRisk;
          }
          return risk;
        })
      }));
    } catch (error) {
      console.error('Error updating risk:', error);
      throw error;
    }
  },

  deleteRisk: async (id) => {
    try {
      const { error } = await supabase
        .from('risks')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set((state) => ({
        risks: state.risks.filter(r => r.id !== id)
      }));
    } catch (error) {
      console.error('Error deleting risk:', error);
      throw error;
    }
  },

  addTask: (task) => {
    const newTask: TaskRecord = {
      ...task,
      id: generateId()
    };
    set((state) => ({
      tasks: [...state.tasks, newTask]
    }));
  },

  updateTask: (id, updates) => {
    set((state) => ({
      tasks: state.tasks.map(task => 
        task.id === id ? { ...task, ...updates } : task
      )
    }));
  },

  deleteTask: (id) => {
    set((state) => ({
      tasks: state.tasks.filter(t => t.id !== id)
    }));
  },

  completeTask: (id) => {
    set((state) => {
      const taskIndex = state.tasks.findIndex(t => t.id === id);
      if (taskIndex === -1) return state;
      
      const task = state.tasks[taskIndex];
      const updatedTasks = [...state.tasks];
      
      // Create evidence for completed task
      const evidence: EvidenceRecord = {
        id: generateId(),
        taskId: id,
        nodeId: task.nodeId,
        title: `Task completed: ${task.title}`,
        type: 'note',
        note: `Task "${task.title}" was completed on ${dayjs().format('YYYY-MM-DD HH:mm')}`,
        created_at: dayjs().toISOString()
      };
      
      // Update task status and roll to next due date
      updatedTasks[taskIndex] = {
        ...task,
        status: 'Done',
        last_run: dayjs().toISOString()
      };
      
      // Calculate next due date based on cadence
      const nextDue = dayjs(task.due_next);
      switch (task.cadence) {
        case 'daily':
          updatedTasks[taskIndex].due_next = nextDue.add(1, 'day').toISOString();
          break;
        case 'weekly':
          updatedTasks[taskIndex].due_next = nextDue.add(1, 'week').toISOString();
          break;
        case 'monthly':
          updatedTasks[taskIndex].due_next = nextDue.add(1, 'month').toISOString();
          break;
        case 'quarterly':
          updatedTasks[taskIndex].due_next = nextDue.add(3, 'month').toISOString();
          break;
        case 'yearly':
          updatedTasks[taskIndex].due_next = nextDue.add(1, 'year').toISOString();
          break;
      }
      
      if (task.cadence !== 'once') {
        updatedTasks[taskIndex].status = 'Scheduled';
      }

      return {
        ...state,
        tasks: updatedTasks,
        evidence: [...state.evidence, evidence]
      };
    });
  },

  addEvidence: async (evidence) => {
    try {
      // Get current user's org_id
      const user = await supabase.auth.getUser();
      if (!user.data.user?.id) {
        throw new Error('User not authenticated');
      }
      
      const { data: profile } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('user_id', user.data.user.id)
        .single();
      
      if (!profile?.org_id) {
        throw new Error('User organization not found');
      }

      const evidenceData = {
        name: evidence.title,
        type: evidence.type,
        notes: evidence.note,
        storage_path: evidence.url,
        org_id: profile.org_id
      };

      const { data, error } = await supabase
        .from('evidence')
        .insert(evidenceData)
        .select()
        .single();

      if (error) throw error;

      const newEvidence: EvidenceRecord = {
        id: data.id,
        nodeId: evidence.nodeId,
        riskId: evidence.riskId,
        taskId: evidence.taskId,
        title: evidence.title,
        type: evidence.type,
        url: evidence.url,
        note: evidence.note,
        created_at: data.created_at
      };

      set((state) => ({
        evidence: [...state.evidence, newEvidence]
      }));
    } catch (error) {
      console.error('Error adding evidence:', error);
      throw error;
    }
  },

  updateEvidence: (id, updates) => {
    set((state) => ({
      evidence: state.evidence.map(evidence => 
        evidence.id === id ? { ...evidence, ...updates } : evidence
      )
    }));
  },

  deleteEvidence: (id) => {
    set((state) => ({
      evidence: state.evidence.filter(e => e.id !== id)
    }));
  },

  setSelectedNode: (id) => {
    set({ selectedNodeId: id });
  },

  updateFilters: (filters) => {
    set((state) => ({
      filters: { ...state.filters, ...filters }
    }));
  },

  getNodesByParent: (parentId) => {
    return get().nodes.filter(node => node.data.parentId === parentId);
  },

  getRisksByNode: (nodeId) => {
    return get().risks.filter(risk => risk.nodeId === nodeId);
  },

  getTasksByNode: (nodeId) => {
    return get().tasks.filter(task => task.nodeId === nodeId);
  },

  getEvidenceByNode: (nodeId) => {
    return get().evidence.filter(evidence => evidence.nodeId === nodeId);
  },

  calculateRiskScore: (likelihood, impact) => {
    const score = likelihood * impact;
    const severity = calculateRiskSeverity(score);
    return { score, severity };
  },

  loadAssets: async () => {
    try {
      set({ loading: true });
      
      // Get current user's org_id
      const user = await supabase.auth.getUser();
      if (!user.data.user?.id) return;
      
      const { data: profile } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('user_id', user.data.user.id)
        .single();
      
      if (!profile?.org_id) {
        set({ loading: false });
        return;
      }
      
      // Load both endpoints and cloud services
      const [endpointsResult, cloudServicesResult] = await Promise.all([
        supabase
          .from('endpoints')
          .select('*')
          .eq('org_id', profile.org_id),
        supabase
          .from('cloud_services')
          .select('*')
          .eq('org_id', profile.org_id)
      ]);

      if (endpointsResult.error) throw endpointsResult.error;
      if (cloudServicesResult.error) throw cloudServicesResult.error;

      // Convert endpoints to React Flow nodes
      const endpointNodes = (endpointsResult.data || []).map(asset => {
        const metadata = (asset.metadata as SupabaseAssetMetadata | null) ?? null;
        const hierarchyType = metadata?.hierarchyType as NodeBase['type'] | undefined;

        return {
          id: asset.id,
          type: 'asset',
          position: {
            x: asset.position_x || Math.random() * 400,
            y: asset.position_y || Math.random() * 400
          },
          data: {
            id: asset.id,
            type: hierarchyType || (asset.type as NodeBase['type']) || 'endpoint',
            label: asset.name,
            category: asset.category || undefined,
            domain: metadata?.domain,
            purdue_level: metadata?.purdue_level,
            parentId: metadata?.parentId,
            meta: metadata ?? undefined
          }
        } as Node<NodeBase>;
      });

      // Convert cloud services to React Flow nodes
      const cloudServiceNodes = (cloudServicesResult.data || []).map(cloudService => {
        return {
          id: cloudService.id,
          type: 'asset',
          position: {
            x: Math.random() * 400,
            y: Math.random() * 400
          },
          data: {
            id: cloudService.id,
            type: 'cloud' as NodeBase['type'],
            label: cloudService.name,
            category: cloudService.provider,
            domain: 'Cloud' as Domain,
            meta: {
              provider: cloudService.provider,
              environment: cloudService.environment,
              criticality: cloudService.criticality,
              region: cloudService.region,
              notes: cloudService.notes
            }
          }
        } as Node<NodeBase>;
      });

      // Combine both node types
      const allNodes = [...endpointNodes, ...cloudServiceNodes];

      set({ nodes: allNodes, loading: false });
    } catch (error) {
      console.error('Error loading assets:', error);
      set({ loading: false });
    }
  },

  loadRisks: async () => {
    try {
      // Get current user's org_id
      const user = await supabase.auth.getUser();
      if (!user.data.user?.id) return;
      
      const { data: profile } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('user_id', user.data.user.id)
        .single();
      
      if (!profile?.org_id) return;
      
      const { data: risks, error } = await supabase
        .from('risks')
        .select('*')
        .eq('org_id', profile.org_id);

      if (error) throw error;

      // Convert Supabase risks to RiskRecord format
      const riskRecords: RiskRecord[] = (risks || []).map(risk => {
        return {
          id: risk.id,
          nodeId: risk.asset_or_process || '',
          title: risk.title,
          description: risk.cause || '',
          likelihood: Math.min(Math.max(risk.likelihood || 3, 1), 5) as 1 | 2 | 3 | 4 | 5,
          impact: Math.min(Math.max(risk.impact || 3, 1), 5) as 1 | 2 | 3 | 4 | 5,
          score: risk.risk_score || 9,
          severity: calculateRiskSeverity(risk.risk_score || 9),
          status: normalizeRiskStatus(risk.status),
          created_at: risk.created_at,
          updated_at: risk.updated_at
        };
      });

      set({ risks: riskRecords });
    } catch (error) {
      console.error('Error loading risks:', error);
    }
  },

  loadEvidence: async () => {
    try {
      // Get current user's org_id
      const user = await supabase.auth.getUser();
      if (!user.data.user?.id) return;
      
      const { data: profile } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('user_id', user.data.user.id)
        .single();
      
      if (!profile?.org_id) return;
      
      const { data: evidence, error } = await supabase
        .from('evidence')
        .select('*')
        .eq('org_id', profile.org_id);

      if (error) throw error;

      // Convert Supabase evidence to EvidenceRecord format
      const evidenceRecords: EvidenceRecord[] = (evidence || []).map(item => ({
        id: item.id,
        nodeId: undefined, // We'll need to map this from asset relationships
        title: item.name,
        type: (item.type === 'note' || item.type === 'url' || item.type === 'file') ? item.type : 'note',
        url: item.storage_path || undefined,
        note: item.notes || undefined,
        created_at: item.created_at
      }));

      set({ evidence: evidenceRecords });
    } catch (error) {
      console.error('Error loading evidence:', error);
    }
  },

  exportData: () => {
    const state = get();
    return JSON.stringify({
      nodes: state.nodes,
      edges: state.edges,
      risks: state.risks,
      tasks: state.tasks,
      evidence: state.evidence
    }, null, 2);
  },

  importData: (data) => {
    try {
      const parsed = JSON.parse(data);
      set({
        nodes: parsed.nodes || [],
        edges: parsed.edges || [],
        risks: parsed.risks || [],
        tasks: parsed.tasks || [],
        evidence: parsed.evidence || []
      });
    } catch (error) {
      console.error('Failed to import data:', error);
    }
  },

  initializeSeedData: () => {
    // Clear existing data
    set({
      nodes: [],
      edges: [],
      risks: [],
      tasks: [],
      evidence: []
    });

    // Create seed nodes
    const seedNodes: Node<NodeBase>[] = [
      {
        id: 'temp-site1',
        type: 'asset',
        position: { x: 100, y: 100 },
        data: {
          id: 'temp-site1',
          type: 'site',
          label: 'Site A',
          meta: {
            location: 'Production Facility',
            description: 'Main production site'
          }
        }
      },
      {
        id: 'temp-zone-it',
        type: 'asset',
        position: { x: 300, y: 100 },
        data: {
          id: 'temp-zone-it',
          type: 'zone',
          label: 'IT Zone',
          parentId: 'temp-site1',
          meta: {
            zoneType: 'IT',
            description: 'Information Technology zone'
          }
        }
      },
      {
        id: 'temp-zone-ot',
        type: 'asset',
        position: { x: 300, y: 250 },
        data: {
          id: 'temp-zone-ot',
          type: 'zone',
          label: 'OT Zone',
          parentId: 'temp-site1',
          meta: {
            zoneType: 'OT',
            description: 'Operational Technology zone'
          }
        }
      }
    ];

    set({ nodes: seedNodes });
  }
}));